<template>
  <div
    :class="thumbnailBoxClass"
    :style="{ height: size, width: size }"
    :title="title"
  >
    <!-- Using v-show instead of v-if to avoid flickering as v-if removes dom elements.  -->
    <slot>
      <img
        v-show="shouldShowImage"
        :src="src"
        :class="thumbnailClass"
        @load="onImgLoad"
        @error="onImgError"
      />
      <Avatar
        v-show="!shouldShowImage"
        :username="userNameWithoutEmoji"
        :class="thumbnailClass"
        :size="avatarSize"
      />
    </slot>
    <img v-if="badgeSrc" class="source-badge" :src="badgeSrc" alt="Badge" />
    <div
      v-if="showStatusIndicator"
      :class="`source-badge user-online-status user-online-status--${status}`"
      :style="statusStyle"
    />
  </div>
</template>
<script>
import Avatar from './Avatar.vue';
import { removeEmoji } from 'shared/helpers/emoji';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import websiteBadge from 'dashboard/assets/images/inbox_icons/website-badge.svg';
import whatsappBadge from 'dashboard/assets/images/inbox_icons/whatsapp-badge.svg';
import messengerBadge from 'dashboard/assets/images/inbox_icons/messenger-badge.svg';
import facebookBadge from 'dashboard/assets/images/inbox_icons/facebook-badge.svg';
import telegramBadge from 'dashboard/assets/images/inbox_icons/telegram-badge.svg';
import lineBadge from 'dashboard/assets/images/inbox_icons/line-badge.svg';
import tiktokBadge from 'dashboard/assets/images/inbox_icons/tiktok-badge.svg';
import instagramBadge from 'dashboard/assets/images/inbox_icons/Instagram-color.svg';
import gmailBadge from 'dashboard/assets/images/inbox_icons/gmail-badge.svg';
import microsoftBadge from 'dashboard/assets/images/inbox_icons/microsoft-badge.svg';

const BADGE_ICONS = {
  [INBOX_TYPES.WEB]: websiteBadge,
  [INBOX_TYPES.WHATSAPP]: whatsappBadge,
  [INBOX_TYPES.FB]: messengerBadge,
  [INBOX_TYPES.FB2]: facebookBadge,
  [INBOX_TYPES.TELEGRAM]: telegramBadge,
  [INBOX_TYPES.LINE]: lineBadge,
  [INBOX_TYPES.TIKTOK]: tiktokBadge,
  'Channel::Api_wa_v1': whatsappBadge,
  'Channel::Api_wa_v2': whatsappBadge,
  'Channel::Api_wa_v3': whatsappBadge,
  'Channel::Email_gmail': gmailBadge,
  'Channel::Email_microsoft_api': microsoftBadge,
  whatsapp: whatsappBadge,
  facebook: messengerBadge,
  facebook_comment: facebookBadge,
  instagram_direct_message: instagramBadge,
  instagram_comment: instagramBadge,
};

export default {
  components: {
    Avatar,
  },
  props: {
    src: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: '40px',
    },
    badge: {
      type: String,
      default: '',
    },
    username: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
    hasBorder: {
      type: Boolean,
      default: false,
    },
    shouldShowStatusAlways: {
      type: Boolean,
      default: false,
    },
    enabledStatusIndicator: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'circle',
    },
  },
  data() {
    return {
      hasImageLoaded: false,
      imgError: false,
    };
  },
  computed: {
    userNameWithoutEmoji() {
      return removeEmoji(this.username);
    },
    showStatusIndicator() {
      if (!this.enabledStatusIndicator) return false;
      if (this.shouldShowStatusAlways) return true;
      return this.status === 'online' || this.status === 'busy';
    },
    avatarSize() {
      return Number(this.size.replace(/\D+/g, ''));
    },
    badgeSrc() {
      return BADGE_ICONS[this.badge] || null;
    },
    badgeStyle() {
      const size = Math.floor(this.avatarSize / 3);
      const badgeSize = `${size + 2}px`;
      const borderRadius = `${size / 2}px`;
      return { width: badgeSize, height: badgeSize, borderRadius };
    },
    statusStyle() {
      const statusSize = `${this.avatarSize / 4}px`;
      return { width: statusSize, height: statusSize };
    },
    thumbnailClass() {
      const className = this.hasBorder
        ? 'border border-solid border-white dark:border-slate-700/50'
        : '';
      const variant =
        this.variant === 'circle' ? 'thumbnail-rounded' : 'thumbnail-square';
      return `user-thumbnail ${className} ${variant}`;
    },
    thumbnailBoxClass() {
      const boxClass = this.variant === 'circle' ? 'is-rounded' : '';
      return `user-thumbnail-box ${boxClass}`;
    },
    shouldShowImage() {
      if (!this.src) {
        return false;
      }
      if (this.hasImageLoaded) {
        return !this.imgError;
      }
      return false;
    },
  },
  watch: {
    src(value, oldValue) {
      if (value !== oldValue && this.imgError) {
        this.imgError = false;
      }
    },
  },
  methods: {
    onImgError() {
      this.imgError = true;
    },
    onImgLoad() {
      this.hasImageLoaded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-thumbnail-box {
  flex: 0 0 auto;
  max-width: 100%;
  position: relative;

  &.is-rounded {
    border-radius: 50%;
  }

  .user-thumbnail {
    border-radius: 50%;
    &.thumbnail-square {
      border-radius: var(--border-radius-large);
    }
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    object-fit: cover;
    vertical-align: initial;
  }

  .source-badge {
    border-radius: 100%;
    padding: 2px;
    position: absolute;
    right: -3px;
    bottom: -3px;
    height: 1rem;
    width: 1rem;
    @apply bg-white dark:bg-slate-900;
  }

  .user-online-status {
    border-radius: 50%;
    top: 0px;
    right: 0px;

    &:after {
      content: ' ';
    }
  }

  .user-online-status--online {
    @apply bg-green-400 dark:bg-green-400;
  }

  .user-online-status--busy {
    @apply bg-yellow-500 dark:bg-yellow-500;
  }

  .user-online-status--offline {
    @apply bg-slate-500 dark:bg-slate-500;
  }
}
</style>
